import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import {
  Menu, X, ChevronRight, Phone, Mail, Calendar,
  Home, Users, Briefcase, Scale, HeartPulse, Calculator,
  FileText, Building2, BookOpen, Wrench, MessageSquare, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import logoHeader from "@/assets/logo-header.webp";
import type { LucideIcon } from "lucide-react";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language } = useLanguage();
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const practiceAreas = [
    { titleKey: "practices.immigration" as const, href: "/practice-areas/immigration-services", descKey: "practices.immigration.desc" as const, icon: Globe },
    { titleKey: "practices.criminal" as const, href: "/practice-areas/criminal-defense", descKey: "practices.criminal.desc" as const, icon: Scale },
    { titleKey: "practices.injury" as const, href: "/practice-areas/personal-injury", descKey: "practices.injury.desc" as const, icon: HeartPulse },
    { titleKey: "practices.family" as const, href: "/practice-areas/family-law", descKey: "practices.family.desc" as const, icon: Users },
    { titleKey: "practices.tax" as const, href: "/practice-areas/tax-law", descKey: "practices.tax.desc" as const, icon: Calculator },
    { titleKey: "practices.probate" as const, href: "/practice-areas/probate-estate", descKey: "practices.probate.desc" as const, icon: FileText },
    { titleKey: "practices.realestate" as const, href: "/practice-areas/real-estate-law", descKey: "practices.realestate.desc" as const, icon: Building2 },
    { titleKey: "practices.business" as const, href: "/practice-areas/business-law", descKey: "practices.business.desc" as const, icon: Briefcase },
  ] as const;

  const navLinks: { to: string; labelKey: string; icon: LucideIcon }[] = [
    { to: "/", labelKey: "header.home", icon: Home },
    { to: "/about-us", labelKey: "header.about", icon: Users },
  ];

  const secondaryLinks: { to: string; labelKey: string; icon: LucideIcon }[] = [
    { to: "/blog", labelKey: "header.blog", icon: BookOpen },
    { to: "/tools", labelKey: "header.tools", icon: Wrench },
    { to: "/contact", labelKey: "header.contact", icon: MessageSquare },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/" || location.pathname === "";
    return location.pathname.startsWith(path);
  };

  const isPracticeActive = practiceAreas.some(a => location.pathname.startsWith(a.href));

  const closeMobile = () => setMobileMenuOpen(false);

  const MobileNavLink = ({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) => (
    <LocalizedLink
      to={to}
      className={cn(
        "flex items-center gap-3 py-3 px-4 text-[15px] font-medium rounded-xl transition-all active:scale-[0.98]",
        isActive(to)
          ? "bg-primary/10 text-primary"
          : "text-foreground hover:bg-muted/60"
      )}
      onClick={closeMobile}
    >
      <div className={cn(
        "h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-colors",
        isActive(to) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
      )}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <span className="flex-1">{label}</span>
      <ChevronRight className={cn("h-4 w-4 transition-colors", isActive(to) ? "text-primary/60" : "text-muted-foreground/40")} />
    </LocalizedLink>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Top bar - desktop only */}
      <div className="hidden lg:block bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-4 xl:gap-6">
              <a href="tel:8329303059" className="flex items-center gap-2 hover:text-accent transition-colors">
                <Phone className="h-4 w-4" />
                <span className="font-medium">EN: (832) 930-3059</span>
              </a>
              <a href="tel:8323567254" className="flex items-center gap-2 hover:text-accent transition-colors">
                <Phone className="h-4 w-4" />
                <span className="font-medium">ES: (832) 356-7254</span>
              </a>
              <a href="tel:8329876543" className="flex items-center gap-2 hover:text-accent transition-colors">
                <Phone className="h-4 w-4" />
                <span className="font-medium">VI: (832) 930-3059</span>
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a href="mailto:info@danielalbertlaw.com" className="flex items-center gap-2 hover:text-accent transition-colors">
                <Mail className="h-4 w-4" />
                <span>info@danielalbertlaw.com</span>
              </a>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation bar */}
      <div className="container mx-auto px-4">
        <div className="flex h-16 lg:h-20 items-center justify-between">
          {/* Logo */}
          <LocalizedLink to="/" className="flex items-center" onClick={closeMobile}>
            <img
              src={logoHeader}
              alt="Daniel Albert Law Firm"
              className="h-10 md:h-14 w-auto"
              width="423"
              height="57"
            />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <LocalizedLink to="/" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.home')}
            </LocalizedLink>
            <LocalizedLink to="/about-us" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.about')}
            </LocalizedLink>
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-foreground hover:text-primary font-medium">
                    {t('header.practices')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid gap-3 p-6 w-[600px] grid-cols-2">
                      {practiceAreas.map((area) => (
                        <LocalizedLink
                          key={area.href}
                          to={area.href}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="text-sm font-medium leading-none">{t(area.titleKey)}</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">{t(area.descKey)}</p>
                        </LocalizedLink>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            <LocalizedLink to="/blog" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.blog')}
            </LocalizedLink>
            <LocalizedLink to="/tools" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.tools')}
            </LocalizedLink>
            <LocalizedLink to="/contact" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.contact')}
            </LocalizedLink>
            <Button asChild variant="hero" size="lg">
              <LocalizedLink to="/booking">{t('header.consultation')}</LocalizedLink>
            </Button>
          </nav>

          {/* Mobile controls */}
          <div className="lg:hidden flex items-center gap-1.5">
            <LanguageSwitcher />
            <button
              className={cn(
                "relative h-11 w-11 flex items-center justify-center rounded-xl transition-all duration-200",
                mobileMenuOpen
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              <div className="relative h-6 w-6">
                <Menu className={cn(
                  "h-6 w-6 absolute inset-0 transition-all duration-200",
                  mobileMenuOpen ? "opacity-0 rotate-90 scale-75" : "opacity-100 rotate-0 scale-100"
                )} />
                <X className={cn(
                  "h-6 w-6 absolute inset-0 transition-all duration-200",
                  mobileMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-75"
                )} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      {/* Backdrop */}
      <div
        className={cn(
          "lg:hidden fixed inset-0 top-16 z-[60] bg-foreground/25 backdrop-blur-[2px] transition-opacity duration-200",
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={closeMobile}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={cn(
          "lg:hidden fixed inset-x-0 top-16 bottom-0 z-[70] bg-background transition-all duration-300",
          mobileMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
        )}
      >
        <nav className="h-full flex flex-col overflow-hidden">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 pt-3 pb-4 space-y-1">
            {/* Main navigation links */}
            {navLinks.map((link) => (
              <MobileNavLink key={link.to} to={link.to} label={t(link.labelKey as any)} icon={link.icon} />
            ))}

            {/* Practice Areas Accordion */}
            <Accordion type="single" collapsible defaultValue={isPracticeActive ? "practice-areas" : undefined}>
              <AccordionItem value="practice-areas" className="border-none">
                <AccordionTrigger className={cn(
                  "py-3 px-4 text-[15px] font-medium rounded-xl hover:no-underline transition-all [&>svg]:h-4 [&>svg]:w-4",
                  isPracticeActive
                    ? "bg-primary/10 text-primary [&>svg]:text-primary/60"
                    : "text-foreground hover:bg-muted/60 [&>svg]:text-muted-foreground/40"
                )}>
                  <span className="flex items-center gap-3">
                    <div className={cn(
                      "h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                      isPracticeActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    )}>
                      <Scale className="h-[18px] w-[18px]" />
                    </div>
                    {t('header.practices')}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-2">
                  <div className="grid grid-cols-2 gap-1.5 px-1">
                    {practiceAreas.map((area) => {
                      const AreaIcon = area.icon;
                      const active = location.pathname.startsWith(area.href);
                      return (
                        <LocalizedLink
                          key={area.href}
                          to={area.href}
                          className={cn(
                            "flex flex-col items-center gap-1.5 py-3 px-2 text-center rounded-xl transition-all active:scale-[0.97]",
                            active
                              ? "bg-primary/10 text-primary"
                              : "text-foreground hover:bg-muted/60"
                          )}
                          onClick={closeMobile}
                        >
                          <div className={cn(
                            "h-8 w-8 rounded-lg flex items-center justify-center transition-colors",
                            active ? "bg-primary text-primary-foreground" : "bg-muted/80 text-muted-foreground"
                          )}>
                            <AreaIcon className="h-4 w-4" />
                          </div>
                          <span className="text-xs font-medium leading-tight">{t(area.titleKey)}</span>
                        </LocalizedLink>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Secondary links */}
            {secondaryLinks.map((link) => (
              <MobileNavLink key={link.to} to={link.to} label={t(link.labelKey as any)} icon={link.icon} />
            ))}

            {/* Divider */}
            <div className="!my-3 border-t border-border" />

            {/* Quick contact */}
            <div className="grid grid-cols-2 gap-2">
              <a href="tel:8329303059" className="flex items-center gap-2 py-2.5 px-3 rounded-xl bg-muted/40 hover:bg-muted/70 transition-colors active:scale-[0.98]">
                <Phone className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground leading-none">English</p>
                  <p className="text-xs font-medium text-foreground truncate">(832) 930-3059</p>
                </div>
              </a>
              <a href="tel:8323567254" className="flex items-center gap-2 py-2.5 px-3 rounded-xl bg-muted/40 hover:bg-muted/70 transition-colors active:scale-[0.98]">
                <Phone className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground leading-none">Español</p>
                  <p className="text-xs font-medium text-foreground truncate">(832) 356-7254</p>
                </div>
              </a>
            </div>
            <a href="mailto:info@danielalbertlaw.com" className="flex items-center gap-2 py-2.5 px-3 rounded-xl bg-muted/40 hover:bg-muted/70 transition-colors active:scale-[0.98]">
              <Mail className="h-4 w-4 text-primary shrink-0" />
              <p className="text-xs font-medium text-foreground truncate">info@danielalbertlaw.com</p>
            </a>
          </div>

          {/* Sticky CTA bottom */}
          <div className="shrink-0 p-4 border-t border-border bg-background/95 backdrop-blur safe-area-bottom">
            <Button asChild variant="hero" className="w-full min-h-[52px] text-base font-semibold shadow-lg">
              <LocalizedLink to="/booking" onClick={closeMobile}>
                <Calendar className="h-5 w-5 mr-2" />
                {t('header.consultation')}
              </LocalizedLink>
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
